import React from 'react';
import {Table, Tag, Button, Space, Modal} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {useTranslation} from 'react-i18next';
import {SorterType} from "../graphql/queries/getSorterListing";
import {EditOutlined, DeleteOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';

interface SorterColumnsProps {
    sorterData: SorterType[];
    loading: boolean;
    onEdit: (record: SorterType) => void;
    onDelete: (id: string) => void;
    onResetBags?: (id: string) => void;
    onPrintChuteLabels?: (record: SorterType) => void;
}

const SorterColumns: React.FC<SorterColumnsProps> = ({
    sorterData,
    loading,
    onEdit,
    onDelete,
    onResetBags,
    onPrintChuteLabels,
}) => {
    const {t} = useTranslation();

    const showDeleteConfirm = (record: SorterType) => {
        Modal.confirm({
            title: t('wms.sorter.messages.deleteConfirm'),
            icon: <ExclamationCircleOutlined />,
            content: t('wms.sorter.messages.deleteWarning'),
            okText: t('wms.sorter.messages.deleteConfirmYes'),
            okType: 'danger',
            cancelText: t('wms.sorter.messages.deleteConfirmNo'),
            centered: true,
            onOk() {
                onDelete(record.id);
            },
        });
    };

    const showResetBagsConfirm = (record: SorterType) => {
        Modal.confirm({
            title: t('wms.sorter.messages.resetBagsConfirm'),
            icon: <ExclamationCircleOutlined />,
            content: t('wms.sorter.messages.resetBagsWarning'),
            okText: t('wms.sorter.messages.resetBagsConfirmYes'),
            okType: 'primary',
            cancelText: t('wms.sorter.messages.resetBagsConfirmNo'),
            centered: true,
            onOk() {
                onResetBags?.(record.id);
            },
        });
    };

    // 判断是否显示重置包牌按钮：交叉带(2) / 直线分拣机(5) 才有格口方案，才谈得上清空包牌
    const shouldShowResetBags = (record: SorterType): boolean => {
        return record.sorterType === 2 || record.sorterType === 5;
    };

    // 判断是否显示打印格口标签按钮：交叉带(2) / 直线分拣机(5) 才有格口方案
    const shouldShowPrintChuteLabels = (record: SorterType): boolean => {
        return record.sorterType === 2 || record.sorterType === 5;
    };

    const getSorterStatusTag = (status: number) => {
        switch (status) {
            case 0:
                return <Tag color="default">{t('wms.sorter.status.unused')}</Tag>;
            case 1:
                return <Tag color="success">{t('wms.sorter.status.inUse')}</Tag>;
            default:
                return <Tag color="default">{t('wms.sorter.status.unknown')}</Tag>;
        }
    };

    const getSorterTypeTag = (type: number) => {
        switch (type) {
            case 1:
                return <Tag color="blue">{t('wms.sorter.type.dws')}</Tag>;
            case 2:
                return <Tag color="green">{t('wms.sorter.type.crossBelt')}</Tag>;
            case 3:
                return <Tag color="orange">{t('wms.sorter.type.smallParcelScanner')}</Tag>;
            case 4:
                return <Tag color="purple">{t('wms.sorter.type.rapidScan')}</Tag>;
            case 5:
                return <Tag color="cyan">{t('wms.sorter.type.linearSorter')}</Tag>;
            default:
                return <Tag color="default">{t('wms.sorter.type.unknown')}</Tag>;
        }
    };

    const formatDateTime = (timestamp?: number) => {
        if (!timestamp) return '-';
        return dayjs.unix(timestamp).format('YYYY-MM-DD HH:mm:ss');
    };

    const columns: ColumnsType<SorterType> = [
        {
            title: t('wms.sorter.columns.sorterCode'),
            dataIndex: 'sorterCode',
            key: 'sorterCode',
            width: 150,
            render: (code?: string) => code || '-',
        },
        {
            title: t('wms.sorter.columns.sorterName'),
            dataIndex: 'sorterName',
            key: 'sorterName',
            width: 200,
            render: (name?: string) => name || '-',
        },
        {
            title: t('wms.sorter.columns.sorterIp'),
            dataIndex: 'sorterIp',
            key: 'sorterIp',
            width: 160,
            render: (ip?: string) => ip || '-',
        },
        {
            title: t('wms.sorter.columns.sorterPort'),
            dataIndex: 'sorterPort',
            key: 'sorterPort',
            width: 120,
            render: (port?: string) => port || '-',
        },
        {
            title: t('wms.sorter.columns.status'),
            dataIndex: 'sorterStatus',
            key: 'sorterStatus',
            width: 100,
            render: (status: number) => getSorterStatusTag(status),
        },
        {
            title: t('wms.sorter.columns.type'),
            dataIndex: 'sorterType',
            key: 'sorterType',
            width: 120,
            render: (type: number) => getSorterTypeTag(type),
        },
        {
            title: t('wms.sorter.columns.chuteConfig'),
            dataIndex: 'chuteConfig',
            key: 'chuteConfig',
            width: 200,
            ellipsis: {
                showTitle: true,
            },
            render: (config: string) => {
                if (!config) return '-';
                const maxLength = 40;
                if (config.length > maxLength) {
                    return config.substring(0, maxLength) + '...';
                }
                return config;
            },
        },
        {
            title: t('wms.sorter.columns.disabledChuteConfig'),
            dataIndex: 'disabledChuteConfig',
            key: 'disabledChuteConfig',
            width: 200,
            ellipsis: true,
            render: (config?: string) => config || '-',
        },
        {
            title: t('wms.sorter.columns.actions'),
            key: 'actions',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small" className="justify-start">
                    <Button
                        type="link"
                        size="small"
                        onClick={() => onEdit(record)}
                    >
                        {t('wms.sorter.buttons.edit')}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        danger
                        onClick={() => showDeleteConfirm(record)}
                    >
                        {t('wms.sorter.buttons.delete')}
                    </Button>
                    {shouldShowResetBags(record) && (
                        <Button
                            type="link"
                            size="small"
                            onClick={() => showResetBagsConfirm(record)}
                        >
                            {t('wms.sorter.buttons.resetBags')}
                        </Button>
                    )}
                    {shouldShowPrintChuteLabels(record) && (
                        <Button
                            type="link"
                            size="small"
                            onClick={() => onPrintChuteLabels?.(record)}
                        >
                            {t('wms.sorter.buttons.printChuteLabels')}
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={sorterData}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            className="mb-4"
        />
    );
};

export default SorterColumns;

